/*
 * BbLAB native product recommendations loader.
 *
 * Fills `[data-bblab-recommendations-host]` from Shopify's own
 * `/recommendations/products` section endpoint. The host renders on the product
 * page with the current product id, the section id and the locale-aware
 * `routes.product_recommendations_url`; this file only reads the same-origin
 * GET and swaps in the server-rendered host. It never invents a product and
 * never clears a real fallback.
 *
 * Contract:
 *   - same-origin GET only; a foreign origin or a non-recommendation path is
 *     rejected before any request is made;
 *   - the request waits until the host nears the viewport (IntersectionObserver,
 *     200px margin); without IntersectionObserver it loads directly;
 *   - an ~8s bounded timeout aborts a stuck request;
 *   - HTTP errors, empty/malformed bodies, a missing host or a section/product
 *     identity mismatch all keep the existing fallback byte-for-byte;
 *   - the response host must be `performed="true"` and match this section and
 *     product; only its content is inserted, scripts are stripped;
 *   - repeated DOMContentLoaded / script execution / section load never sends a
 *     second request for the same host;
 *   - unloading a section unobserve/aborts it and a late response never writes
 *     to a detached or replaced host;
 *   - a swap waits while focus is inside the fallback; a focusout that only
 *     moves focus between two links inside the host keeps waiting, and a real
 *     leave re-checks token/connection/focus after the focus event before it
 *     writes;
 *   - a successful swap fires one `bblab:recommendations:loaded` CustomEvent on
 *     `document` carrying only the host product/section identity;
 *   - inserted cards get the same near-viewport hover-image warm-up as the rest
 *     of the theme; unloading drops this observer's own observations and a late
 *     hover callback never writes to a detached card, and nothing is rebound on
 *     the purchase form or navigation.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__bblabRecommendationsLoaded) return;
  window.__bblabRecommendationsLoaded = true;

  var HOST_SELECTOR = '[data-bblab-recommendations-host]';
  var SECTION_SELECTOR = '[data-bblab-section="related-products"]';
  var TIMEOUT_MS = 8000;
  var ROOT_MARGIN = '200px';
  var HOVER_QUERY = '(hover: hover)';

  var sectionObserver = null;
  var hoverObserver = null;

  function urlConstructor() {
    return window.URL || (typeof URL !== 'undefined' ? URL : null);
  }

  function currentLocation() {
    return window.location || { href: '/', origin: '' };
  }

  // Only a same-origin `/recommendations/products` endpoint (optionally behind a
  // locale prefix such as `/ja/recommendations/products`) may be requested.
  function parseEndpoint(raw) {
    if (!raw) return null;
    var URLCtor = urlConstructor();
    if (!URLCtor) return null;
    var location = currentLocation();
    var url;
    try {
      url = new URLCtor(raw, location.href || '/');
    } catch (error) {
      return null;
    }
    if (!url) return null;
    if (url.origin !== location.origin) return null;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (!/(^|\/)recommendations\/products$/.test(url.pathname)) return null;
    return url.href;
  }

  function stateFor(host) {
    if (!host.__bblabRecommendations) {
      host.__bblabRecommendations = {
        init: false,
        started: false,
        done: false,
        token: 0,
        controller: null,
        pendingNext: null,
        focusBound: false,
        notified: false,
      };
    }
    return host.__bblabRecommendations;
  }

  function isDetached(host) {
    if (typeof host.isConnected === 'boolean') return !host.isConnected;
    if (document.contains) return !document.contains(host);
    return false;
  }

  function focusInside(host) {
    var active = document.activeElement;
    if (!active || active === document.body) return false;
    return !!(host.contains && host.contains(active));
  }

  function stripScripts(node) {
    if (!node.querySelectorAll) return;
    var scripts = node.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i += 1) {
      if (scripts[i].parentNode && scripts[i].parentNode.removeChild) {
        scripts[i].parentNode.removeChild(scripts[i]);
      }
    }
  }

  /* ── hover image: warm the clipped second image of the inserted cards ──── */
  function hoverObserverEl() {
    if (hoverObserver) return hoverObserver;
    hoverObserver = new window.IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        // A late callback may fire after the card was unloaded: never write to a
        // detached node, and drop the stale observation.
        if (isDetached(entry.target)) {
          hoverObserver.unobserve(entry.target);
          return;
        }
        var alternate = entry.target.querySelector('img.alternate');
        if (alternate) alternate.loading = 'eager';
        hoverObserver.unobserve(entry.target);
      });
    }, { rootMargin: ROOT_MARGIN });
    return hoverObserver;
  }

  function warmCards(root) {
    if (!window.matchMedia || !window.matchMedia(HOVER_QUERY).matches) return;
    if (!window.IntersectionObserver) return;
    var observer = hoverObserverEl();
    var medias = root.querySelectorAll ? root.querySelectorAll('.product-media') : [];
    for (var i = 0; i < medias.length; i += 1) {
      var media = medias[i];
      if (media.dataset.hoverObserved === 'true') continue;
      if (!media.querySelector('img.alternate')) continue;
      media.dataset.hoverObserved = 'true';
      observer.observe(media);
    }
  }

  /* ── insertion, deferred while focus is inside the fallback ─────────────── */
  function flushPending(host, state, token) {
    if (token !== state.token) return;
    if (isDetached(host)) return;
    if (!state.pendingNext) return;
    if (focusInside(host)) return;
    var pending = state.pendingNext;
    state.pendingNext = null;
    apply(host, pending, state, token);
  }

  function onFocusOut(host, state, token) {
    return function (event) {
      if (token !== state.token) return;
      if (!state.pendingNext) return;
      // During a focusout the browser leaves document.activeElement at BODY for
      // the duration of the event, so activeElement alone cannot decide. An
      // explicit in-host relatedTarget means focus only moved inside the host:
      // keep the payload pending and wait for the event that really leaves.
      var related = event && event.relatedTarget;
      if (related && host.contains && host.contains(related)) return;
      // Truly leaving, or an unknown target: let the focus event finish, then
      // re-read the token, host connection, pending payload and real focus.
      window.setTimeout(function () {
        flushPending(host, state, token);
      }, 0);
    };
  }

  function apply(host, next, state, token) {
    if (token !== state.token) return;
    if (isDetached(host)) return;
    if (focusInside(host)) {
      // Never yank focus out of the fallback: remember the payload and retry
      // once focus leaves the host.
      state.pendingNext = next;
      if (!state.focusBound && host.addEventListener) {
        state.focusBound = true;
        host.addEventListener('focusout', onFocusOut(host, state, token));
      }
      return;
    }
    state.done = true;
    state.pendingNext = null;
    host.innerHTML = next.innerHTML;
    host.setAttribute('data-recommendations-performed', 'true');
    host.removeAttribute('data-recommendations-pending');
    if (host.style) {
      host.style.height = '';
      host.style.marginBottom = '';
    }
    warmCards(host);
    if (!state.notified) {
      state.notified = true;
      if (typeof window.CustomEvent === 'function') {
        document.dispatchEvent(new window.CustomEvent('bblab:recommendations:loaded', {
          detail: {
            productId: host.getAttribute('data-recommendations-product-id'),
            sectionId: host.getAttribute('data-recommendations-section-id'),
          },
        }));
      }
    }
  }

  /* ── the bounded same-origin GET ────────────────────────────────────────── */
  function request(host) {
    var state = stateFor(host);
    if (state.started || state.done) return;
    var endpoint = parseEndpoint(host.getAttribute('data-recommendations-url'));
    var productId = host.getAttribute('data-recommendations-product-id');
    var sectionId = host.getAttribute('data-recommendations-section-id');
    if (!endpoint || !productId || !sectionId) return;
    if (typeof window.fetch !== 'function') return;
    if (!window.DOMParser) return;

    state.started = true;
    var token = state.token;
    var settled = false;
    var timedOut = false;

    var options = { method: 'GET', credentials: 'same-origin' };
    if (typeof window.AbortController === 'function') {
      state.controller = new window.AbortController();
      options.signal = state.controller.signal;
    }

    var timer = window.setTimeout(function () {
      timedOut = true;
      if (state.controller && state.controller.abort) state.controller.abort();
      settle();
    }, TIMEOUT_MS);

    function settle() {
      if (settled) return;
      settled = true;
      state.controller = null;
      window.clearTimeout(timer);
    }

    window.fetch(endpoint, options).then(function (response) {
      if (!response || response.ok !== true) return null;
      return response.text();
    }).then(function (html) {
      if (token !== state.token || timedOut) return;
      if (typeof html !== 'string' || html.trim() === '') return;
      if (isDetached(host)) return;
      var parsed;
      try {
        parsed = new window.DOMParser().parseFromString(html, 'text/html');
      } catch (error) {
        return;
      }
      if (!parsed || typeof parsed.querySelector !== 'function') return;
      var next = parsed.querySelector(HOST_SELECTOR);
      if (!next) return;
      if (next.getAttribute('data-recommendations-performed') !== 'true') return;
      if (next.getAttribute('data-recommendations-product-id') !== productId) return;
      if (next.getAttribute('data-recommendations-section-id') !== sectionId) return;
      stripScripts(next);
      if (!next.querySelectorAll || next.querySelectorAll(SECTION_SELECTOR).length === 0) return;
      apply(host, next, state, token);
    }).catch(function () {
      // Any transport/abort error keeps the existing fallback untouched.
    }).then(settle);
  }

  /* ── wiring ─────────────────────────────────────────────────────────────── */
  function hostsWithin(scope) {
    var hosts = [];
    if (!scope) return hosts;
    if (scope.matches && scope.matches(HOST_SELECTOR)) hosts.push(scope);
    if (scope.querySelectorAll) {
      var found = scope.querySelectorAll(HOST_SELECTOR);
      for (var i = 0; i < found.length; i += 1) hosts.push(found[i]);
    }
    return hosts;
  }

  function setup(host) {
    var state = stateFor(host);
    if (state.init || state.started || state.done) return;
    state.init = true;
    if (host.getAttribute('data-recommendations-performed') === 'true') {
      state.done = true;
      return;
    }
    if (!parseEndpoint(host.getAttribute('data-recommendations-url'))) return;
    if (!host.getAttribute('data-recommendations-product-id')) return;
    if (!host.getAttribute('data-recommendations-section-id')) return;
    if (typeof window.IntersectionObserver === 'function') {
      if (!sectionObserver) {
        sectionObserver = new window.IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            sectionObserver.unobserve(entry.target);
            request(entry.target);
          });
        }, { rootMargin: ROOT_MARGIN });
      }
      state.observer = sectionObserver;
      sectionObserver.observe(host);
      return;
    }
    // No IntersectionObserver: load directly so the section still enhances.
    request(host);
  }

  // Drop only this script's own hover observation for media inside the
  // unloaded scope; the legacy bblab.js observer is never touched.
  function releaseHover(scope) {
    if (!hoverObserver || !hoverObserver.unobserve) return;
    var medias = [];
    if (scope.matches && scope.matches('.product-media')) medias.push(scope);
    if (scope.querySelectorAll) {
      var found = scope.querySelectorAll('.product-media');
      for (var i = 0; i < found.length; i += 1) medias.push(found[i]);
    }
    for (var j = 0; j < medias.length; j += 1) hoverObserver.unobserve(medias[j]);
  }

  function teardown(scope) {
    var hosts = hostsWithin(scope);
    for (var i = 0; i < hosts.length; i += 1) {
      var host = hosts[i];
      var state = host.__bblabRecommendations;
      if (!state) continue;
      state.token += 1; // invalidate any late response
      state.done = true;
      state.pendingNext = null;
      if (state.observer && state.observer.unobserve) state.observer.unobserve(host);
      if (state.controller && state.controller.abort) state.controller.abort();
      state.controller = null;
    }
    releaseHover(scope);
  }

  function init(scope) {
    var hosts = hostsWithin(scope || document);
    for (var i = 0; i < hosts.length; i += 1) setup(hosts[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { teardown(event.target); });
})();
